import { TriangleAlert } from 'lucide-react';
import { Chip, IconBox, Modal } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { resolveSkillDescription, resolveSkillIcon } from '../data';
import type { Skill } from '../schemas';

interface Props {
  skill: Skill | null;
  onClose: () => void;
}

export function SkillDetailModal({ skill, onClose }: Props) {
  const open = skill !== null;
  if (!skill) {
    return <Modal open={false} onOpenChange={onClose} title="" size="lg" children={null} />;
  }
  const Icon = resolveSkillIcon(skill.name);
  const desc = resolveSkillDescription(skill);
  const hasWarn = skill.status === 'warning';

  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()} title={skill.name} size="lg">
      <Stack gap="md">
        <div className="flex items-center gap-3">
          <IconBox tone={hasWarn ? 'warn' : 'accent'} size="lg">
            <Icon size={20} strokeWidth={1.5} />
          </IconBox>
          <p className="text-sm text-text-2">{desc}</p>
        </div>

        {hasWarn && skill.warn && (
          <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-[color-mix(in_oklab,var(--warning)_10%,transparent)] px-3 py-2 text-xs text-[var(--warning-fg)]">
            <TriangleAlert size={14} strokeWidth={1.5} className="shrink-0 mt-0.5" />
            <span>{skill.warn}</span>
          </div>
        )}

        <dl className="grid grid-cols-[120px_1fr] gap-y-2 gap-x-3 text-xs">
          {skill.os.length > 0 && (
            <Row label="Plataforma">
              <div className="flex flex-wrap gap-1">
                {skill.os.map((o) => (
                  <Chip key={o} tone="neutral" size="sm">
                    {o}
                  </Chip>
                ))}
              </div>
            </Row>
          )}
          {skill.requires.length > 0 && (
            <Row label="Requer">
              <div className="flex flex-wrap gap-1">
                {skill.requires.map((r) => (
                  <Chip key={r} tone="neutral" size="sm">
                    {r}
                  </Chip>
                ))}
              </div>
            </Row>
          )}
          {skill.homepage && (
            <Row label="Homepage">
              <a
                href={skill.homepage}
                target="_blank"
                rel="noreferrer"
                className="text-accent hover:underline truncate inline-block max-w-full"
              >
                {skill.homepage}
              </a>
            </Row>
          )}
        </dl>

        {skill.readme && (
          <div className="border-t border-border pt-3">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
              README
            </span>
            <pre className="mt-2 max-h-72 overflow-auto rounded-md bg-surface p-3 text-xs text-text-2 font-mono whitespace-pre-wrap">
              {skill.readme}
            </pre>
          </div>
        )}
      </Stack>
    </Modal>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-text-muted uppercase tracking-wider text-[10px] font-semibold pt-0.5">
        {label}
      </dt>
      <dd className="text-text-2">{children}</dd>
    </>
  );
}
